import React, { useState, useMemo, useCallback } from "react";
import ThinScroll from "../../shared/ThinScroll";
import Pagination from "../../shared/Pagination";
import useTablePage from "../../../hooks/useTablePage";
import { fmtCurrency } from "../../shared/format.js";

/**
 * DataTable — raw_daily evidence component.
 *
 * Sortable + paginated table, 15 rows per page, newest first by default.
 * CSV download for export. Columns: Date · High · Low · Rain % ·
 * Precip (mm) · Wind · Conditions · Sessions · Bookings · Revenue.
 *
 * Table design mirrors NormalsTable (Season: Arrivals Past Where Any
 * Forecast Reaches) — ThinScroll + responsive-card-table shell, same
 * header/row styling, weekday + short date in the date cell, colour-
 * coded values, data-label attributes for the responsive card view.
 *
 * Pagination uses the shared Pagination component (same as Google Analytics
 * Sources table) with useTablePage for clamped + auto-resetting page state.
 *
 * Column widths are fixed via table-fixed + explicit w-[...] on every header
 * so columns don't reflow when changing pages (same fix as the Sources table).
 *
 * Spec references: §7 (raw_daily), §6 rule 4, §2d (formatting).
 */

/** Format number with locale separators. */
const fmtNum = (v) => (v != null ? Number(v).toLocaleString() : "—");

/** Short date — "Aug 10" */
const shortDate = (dateStr) => {
  if (!dateStr) return "";
  const d = new Date(dateStr + "T00:00:00");
  if (Number.isNaN(d.getTime())) return dateStr;
  return d.toLocaleDateString("en-US", { month: "short", day: "numeric" });
};

/** Day of week — "Sun" */
const dayOfWeek = (dateStr) => {
  if (!dateStr) return "";
  const d = new Date(dateStr + "T00:00:00");
  if (Number.isNaN(d.getTime())) return "";
  return d.toLocaleDateString("en-US", { weekday: "short" });
};

/** Rain % colour — higher is wetter. */
const rainColor = (pct) => {
  if (pct >= 50) return "text-blue-500 dark:text-blue-400";
  if (pct >= 25) return "text-yellow-500 dark:text-yellow-400";
  return "text-gray-700 dark:text-gray-300";
};

/** Build CSV string from rows. */
const toCSV = (rows) => {
  const header = "Date,High (°F),Low (°F),Rain %,Precip (mm),Snow (cm),Wind (mph),Conditions,Source,Sessions,Bookings,Revenue";
  const lines = rows.map((r) =>
    [
      r.date,
      r.temp_high_f,
      r.temp_low_f,
      r.precip_probability_pct,
      r.precip_mm,
      r.snow_cm,
      r.wind_max_mph,
      `"${(r.weather || "").replace(/"/g, '""')}"`,
      r.source,
      r.sessions,
      r.bookings,
      r.revenue,
    ].join(",")
  );
  return [header, ...lines].join("\n");
};

/** Trigger CSV download. */
const downloadCSV = (rows) => {
  const csv = toCSV(rows);
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = "weather-detail.csv";
  a.click();
  URL.revokeObjectURL(url);
};

const COLUMNS = [
  { key: "date",                   label: "Date",        align: "left",   width: "w-[120px]" },
  { key: "temp_high_f",            label: "High",        align: "right",  width: "w-[70px]" },
  { key: "temp_low_f",             label: "Low",         align: "right",  width: "w-[70px]" },
  { key: "precip_probability_pct", label: "Rain %",      align: "right",  width: "w-[70px]" },
  { key: "precip_mm",              label: "Precip",      align: "right",  width: "w-[70px]" },
  { key: "snow_cm",                label: "Snow (cm)",   align: "right",  width: "w-[80px]", sortable: true },
  { key: "wind_max_mph",           label: "Wind",        align: "right",  width: "w-[70px]" },
  { key: "weather",                label: "Conditions",  align: "left",   width: "w-[140px]" },
  { key: "source",                label: "Source",      align: "center", width: "w-[90px]", sortable: true },
  { key: "sessions",               label: "Sessions",    align: "right",  width: "w-[80px]" },
  { key: "bookings",               label: "Bookings",    align: "right",  width: "w-[80px]" },
  { key: "revenue",                label: "Revenue",     align: "right",  width: "w-[100px]" },
];

const DataTable = ({ panel }) => {
  const f = panel.facts || {};
  const rows = f.rows || [];

  const [sortKey, setSortKey] = useState("date");
  const [sortDir, setSortDir] = useState("desc");

  const PAGE_SIZE = 15;

  const handleSort = useCallback((key) => {
    if (key === sortKey) {
      setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    } else {
      setSortKey(key);
      setSortDir(key === "date" ? "desc" : "asc");
    }
  }, [sortKey]);

  const sorted = useMemo(() => {
    const copy = [...rows];
    copy.sort((a, b) => {
      const av = a[sortKey];
      const bv = b[sortKey];
      if (av == null && bv == null) return 0;
      if (av == null) return 1;
      if (bv == null) return -1;
      if (typeof av === "string") {
        return sortDir === "asc" ? av.localeCompare(bv) : bv.localeCompare(av);
      }
      return sortDir === "asc" ? av - bv : bv - av;
    });
    return copy;
  }, [rows, sortKey, sortDir]);

  const totalItems = sorted.length;
  const { page, setPage, totalPages, sliceStart, sliceEnd } = useTablePage(
    totalItems,
    PAGE_SIZE,
    [rows, sortKey, sortDir],
  );
  const pageRows = sorted.slice(sliceStart, sliceEnd);

  if (rows.length === 0) return null;

  return (
    <div className="space-y-3">
      {/* CSV download */}
      <div className="flex items-center justify-end">
        <button
          type="button"
          onClick={() => downloadCSV(rows)}
          className="p-1 rounded-md text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
          title="Export CSV"
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12" />
          </svg>
        </button>
      </div>

      {/* Paginated table — ThinScroll + responsive-card-table shell
          mirrors NormalsTable. table-fixed + explicit column widths
          prevent reflow between pages (same fix as GA Sources table).
          min-w-[1040px] matches the sum of all column widths so columns
          keep their declared width until the container narrows enough to
          trigger horizontal scrolling. */}
      <ThinScroll className="overflow-x-auto responsive-card-table-wrapper">
        <table className="w-full text-sm responsive-card-table table-fixed min-w-[1040px]">
          <thead className="text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400">
            <tr className="border-b border-gray-200 dark:border-gray-800 bg-gray-50 dark:bg-gray-800/50">
              {COLUMNS.map((col) => (
                <th
                  key={col.key}
                  className={`px-4 py-2.5 font-semibold cursor-pointer select-none hover:text-gray-700 dark:hover:text-gray-200 ${col.width} ${
                    col.align === "right" ? "text-right" : col.align === "center" ? "text-center" : "text-left"
                  }`}
                  onClick={() => handleSort(col.key)}
                >
                  <span className="inline-flex items-center gap-1">
                    {col.label}
                    {sortKey === col.key && (
                      <span className="text-gray-700 dark:text-gray-200">
                        {sortDir === "asc" ? "↑" : "↓"}
                      </span>
                    )}
                  </span>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {pageRows.map((row, i) => (
              <tr
                key={row.date || i}
                className="border-b border-gray-100 dark:border-gray-800 transition hover:bg-gray-50 dark:hover:bg-gray-800/50"
              >
                {/* Date — weekday + short date, mirrors NormalsTable */}
                <td data-label="Date" className="px-4 py-2.5 text-left whitespace-nowrap">
                  <span className="text-gray-400 dark:text-gray-500 mr-1">
                    {dayOfWeek(row.date)}
                  </span>
                  <span className="font-medium text-gray-900 dark:text-gray-100">
                    {shortDate(row.date)}
                  </span>
                </td>

                {/* High temp */}
                <td data-label="High" className="px-4 py-2.5 text-right whitespace-nowrap font-mono tabular-nums">
                  <span className="font-semibold text-orange-500 dark:text-orange-400">
                    {row.temp_high_f != null ? `${Math.round(row.temp_high_f)}°` : "—"}
                  </span>
                </td>

                {/* Low temp */}
                <td data-label="Low" className="px-4 py-2.5 text-right whitespace-nowrap font-mono tabular-nums">
                  <span className="font-semibold text-blue-500 dark:text-blue-400">
                    {row.temp_low_f != null ? `${Math.round(row.temp_low_f)}°` : "—"}
                  </span>
                </td>

                {/* Rain % */}
                <td data-label="Rain %" className="px-4 py-2.5 text-right whitespace-nowrap font-mono tabular-nums">
                  <span className={`font-semibold ${rainColor(row.precip_probability_pct)}`}>
                    {row.precip_probability_pct != null ? `${Math.round(row.precip_probability_pct)}%` : "—"}
                  </span>
                </td>

                {/* Precip (mm) */}
                <td data-label="Precip" className="px-4 py-2.5 text-right whitespace-nowrap font-mono tabular-nums text-gray-700 dark:text-gray-300">
                  {row.precip_mm != null ? row.precip_mm : "—"}
                </td>

                {/* Snow (cm) */}
                <td data-label="Snow (cm)" className="px-4 py-2.5 text-right whitespace-nowrap font-mono tabular-nums text-gray-700 dark:text-gray-300">
                  {row.snow_cm != null ? `${row.snow_cm} cm` : "—"}
                </td>

                {/* Wind */}
                <td data-label="Wind" className="px-4 py-2.5 text-right whitespace-nowrap font-mono tabular-nums text-gray-700 dark:text-gray-300">
                  {row.wind_max_mph != null ? row.wind_max_mph : "—"}
                </td>

                {/* Conditions */}
                <td data-label="Conditions" className="px-4 py-2.5 text-left text-gray-500 dark:text-gray-400 truncate">
                  {row.weather != null ? row.weather : "—"}
                </td>

                {/* Source */}
                <td data-label="Source" className="px-4 py-2.5 text-center whitespace-nowrap">
                  <span className={row.source === "captured" ? "text-green-600 dark:text-green-400" : "text-amber-600 dark:text-amber-400"}>
                    {row.source === "captured" ? "Captured" : "Archive"}
                  </span>
                </td>

                {/* Sessions */}
                <td data-label="Sessions" className="px-4 py-2.5 text-right whitespace-nowrap font-mono tabular-nums text-gray-700 dark:text-gray-300">
                  {fmtNum(row.sessions)}
                </td>

                {/* Bookings */}
                <td data-label="Bookings" className="px-4 py-2.5 text-right font-medium text-gray-900 dark:text-gray-100 whitespace-nowrap font-mono tabular-nums">
                  {fmtNum(row.bookings)}
                </td>

                {/* Revenue */}
                <td data-label="Revenue" className="px-4 py-2.5 text-right whitespace-nowrap font-mono tabular-nums">
                  <span className="font-medium text-gray-900 dark:text-gray-100">
                    {fmtCurrency(row.revenue)}
                  </span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </ThinScroll>

      {/* Pagination — shared component, same as GA Sources table */}
      <Pagination
        page={page}
        totalPages={totalPages}
        onPageChange={setPage}
        totalItems={totalItems}
        perPage={PAGE_SIZE}
        label="days"
      />
    </div>
  );
};

export default DataTable;